import { useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../api/apiClient';
import Breadcrumb from '../ui/Breadcrumb';
import Modal from '../ui/Modal';
import { moduleColor } from '../ui/modulePalette';
import { IconAlertTriangle, IconEdit, IconPlus, IconTrash, IconUsers } from '../ui/icons';

function employeeTitle(e) {
  const n = String(e?.display_name || '').trim();
  const mail = String(e?.email || '').trim();
  return n || mail || `Employé #${e?.id ?? ''}`;
}

export default function AdminEmployeesPage({ sites }) {
  const [state, setState] = useState({ status: 'loading', employees: [], error: '' });
  const [modal, setModal] = useState(null);
  const [siteModulesById, setSiteModulesById] = useState({});
  const loadingSitesRef = useRef(new Set());

  const accent = moduleColor('employees', '#dc2626');

  const allSites = useMemo(() => {
    const list = Array.isArray(sites) ? sites : [];
    const next = list
      .filter((s) => s && s.id != null)
      .map((s) => ({ id: Number(s.id), name: String(s.name || '').trim() || `Site #${s.id}` }))
      .filter((s) => Number.isFinite(s.id) && s.id > 0);
    next.sort((a, b) => String(a.name).localeCompare(String(b.name), 'fr'));
    return next;
  }, [sites]);

  const refresh = () => {
    let cancelled = false;
    setState((s) => ({ ...s, status: 'loading', error: '' }));
    api
      .adminEmployees()
      .then((res) => {
        if (cancelled) return;
        if (!res.ok) {
          setState({ status: 'error', employees: [], error: res.error || 'Impossible de charger' });
          return;
        }
        setState({ status: 'ready', employees: res.employees || [], error: '' });
      })
      .catch(() => {
        if (cancelled) return;
        setState({ status: 'error', employees: [], error: 'Impossible de charger' });
      });
    return () => {
      cancelled = true;
    };
  };

  useEffect(() => refresh(), []);

  const sorted = useMemo(() => {
    if (state.status !== 'ready') return [];
    return [...(state.employees || [])].sort((a, b) => employeeTitle(a).localeCompare(employeeTitle(b), 'fr'));
  }, [state.employees, state.status]);

  function openCreate() {
    setModal({
      type: 'edit',
      isNew: true,
      saving: false,
      error: '',
      id: null,
      display_name: '',
      email: '',
      password: '',
      is_active: true,
      staff_status: 'employee',
      site_query: '',
      site_ids: [],
      site_admin_modules: {},
    });
  }

  function openEdit(emp) {
    const e = emp || {};
    const siteIds = (e.sites || []).map((s) => Number(s?.id)).filter((n) => Number.isFinite(n) && n > 0);
    const adminBySiteRaw = e.admin_modules_by_site && typeof e.admin_modules_by_site === 'object' ? e.admin_modules_by_site : {};
    const siteAdminModules = {};
    for (const [sid, keys] of Object.entries(adminBySiteRaw)) {
      if (!Array.isArray(keys)) continue;
      siteAdminModules[String(sid)] = keys.map((x) => String(x || '')).filter(Boolean);
    }
    setModal({
      type: 'edit',
      isNew: false,
      saving: false,
      error: '',
      id: Number(e.id),
      display_name: String(e.display_name || ''),
      email: String(e.email || ''),
      password: '',
      is_active: Boolean(e.is_active),
      staff_status: String(e.staff_status || '') || 'employee',
      site_query: '',
      site_ids: siteIds,
      site_admin_modules: siteAdminModules,
    });
  }

  async function saveEmployee(m) {
    const payload = {
      id: m.isNew ? undefined : Number(m.id),
      display_name: String(m.display_name || '').trim(),
      email: String(m.email || '').trim(),
      password: String(m.password || ''),
      is_active: Boolean(m.is_active),
      staff_status: String(m.staff_status || '').trim() || null,
      site_ids: Array.isArray(m.site_ids) ? m.site_ids.map((x) => Number(x)).filter((n) => Number.isFinite(n) && n > 0) : [],
      site_admin_modules: m.site_admin_modules && typeof m.site_admin_modules === 'object' ? m.site_admin_modules : {},
    };
    if (m.isNew) return api.adminEmployeeCreate(payload);
    return api.adminEmployeeUpdate(payload);
  }

  const visibleSites = useMemo(() => {
    if (modal?.type !== 'edit') return [];
    const q = String(modal.site_query || '').trim().toLowerCase();
    if (!q) return allSites;
    return allSites.filter((s) => String(s.name || '').toLowerCase().includes(q));
  }, [modal, allSites]);

  function toggleSite(siteId) {
    const id = Number(siteId);
    if (!Number.isFinite(id) || id <= 0) return;
    setModal((m) => {
      if (!m || m.type !== 'edit') return m;
      const cur = Array.isArray(m.site_ids) ? m.site_ids : [];
      const set = new Set(cur.map((x) => Number(x)));
      if (set.has(id)) set.delete(id);
      else set.add(id);
      return { ...m, site_ids: Array.from(set) };
    });
  }

  useEffect(() => {
    if (modal?.type !== 'edit') return;
    const selected = Array.isArray(modal.site_ids) ? modal.site_ids : [];
    for (const sid of selected) {
      const id = Number(sid);
      if (!Number.isFinite(id) || id <= 0) continue;
      if (siteModulesById[String(id)]?.status === 'ready') continue;
      if (loadingSitesRef.current.has(String(id))) continue;
      loadingSitesRef.current.add(String(id));
      setSiteModulesById((s) => ({
        ...(s || {}),
        [String(id)]: { status: 'loading', modules: [], error: '' },
      }));
      api
        .adminSiteModules(id)
        .then((res) => {
          if (!res.ok) {
            setSiteModulesById((s) => ({
              ...(s || {}),
              [String(id)]: { status: 'error', modules: [], error: res.error || 'Impossible de charger' },
            }));
            return;
          }
          setSiteModulesById((s) => ({
            ...(s || {}),
            [String(id)]: { status: 'ready', modules: res.modules || [], error: '' },
          }));
        })
        .catch(() => {
          setSiteModulesById((s) => ({
            ...(s || {}),
            [String(id)]: { status: 'error', modules: [], error: 'Impossible de charger' },
          }));
        })
        .finally(() => {
          loadingSitesRef.current.delete(String(id));
        });
    }
  }, [modal, siteModulesById]);

  function toggleSiteModuleAdmin(siteId, moduleKey) {
    const sid = String(siteId || '');
    const key = String(moduleKey || '');
    if (!sid || !key) return;
    setModal((m) => {
      if (!m || m.type !== 'edit') return m;
      const base = m.site_admin_modules && typeof m.site_admin_modules === 'object' ? m.site_admin_modules : {};
      const current = Array.isArray(base[sid]) ? base[sid].map((x) => String(x || '')).filter(Boolean) : [];
      const set = new Set(current);
      if (set.has(key)) set.delete(key);
      else set.add(key);
      return { ...m, site_admin_modules: { ...base, [sid]: Array.from(set) } };
    });
  }

  function staffStatusLabel(key) {
    const k = String(key || '').toLowerCase();
    if (k === 'freelance') return 'Freelance';
    if (k === 'apprentice') return 'Apprenti';
    if (k === 'intern') return 'Stagiaire';
    return 'Employé';
  }

  return (
    <div className="Page">
      <header className="PageHeader">
        <div className="PageHeaderBar">
          <Breadcrumb items={[{ label: 'Employés' }]} />
          <div className="Badges">
            <button type="button" className="Button" onClick={openCreate}>
              <span className="ButtonInline">
                <IconPlus /> Nouvel employé
              </span>
            </button>
          </div>
        </div>
      </header>

      {state.error ? (
        <div className="Card" style={{ marginBottom: 14 }}>
          <div className="Error" role="alert">
            {state.error}
          </div>
        </div>
      ) : null}

      <div className="Card">
        <div className="Row" style={{ marginBottom: 12 }}>
          <div className="TileTitle">Liste</div>
        </div>

        {state.status === 'loading' ? <div className="Muted">Chargement…</div> : null}

        {state.status === 'ready' ? (
          <div className="List" role="list">
            {sorted.map((e) => (
              <div key={e.id} className="ListItem" role="listitem">
                <div style={{ minWidth: 0 }}>
                  <div className="UserName OneLine">{employeeTitle(e)}</div>
                  <div className="Muted" style={{ marginTop: 6 }}>
                    {String(e.email || '').trim() ? String(e.email).trim() : '—'}
                  </div>
                  <div className="Badges" style={{ justifyContent: 'flex-start', marginTop: 10 }}>
                    <span className="Badge" style={{ borderColor: accent, color: accent }}>
                      <span className="ButtonInline" style={{ gap: 8 }}>
                        <IconUsers /> {(e.sites || []).length} site(s)
                      </span>
                    </span>
                    <span className="Badge">{staffStatusLabel(e.staff_status)}</span>
                    {!e.is_active ? <span className="Badge">désactivé</span> : <span className="Badge">actif</span>}
                  </div>
                  {(e.sites || []).length ? (
                    <div className="Muted OneLine" style={{ marginTop: 8 }}>
                      {(e.sites || []).map((s) => s.name).join(' · ')}
                    </div>
                  ) : null}
                </div>
                <div className="Badges">
                  <button type="button" className="Button Button--ghost" onClick={() => openEdit(e)}>
                    <span className="ButtonInline">
                      <IconEdit /> Modifier
                    </span>
                  </button>
                  <button
                    type="button"
                    className="Button Button--ghost Button--danger"
                    onClick={() => setModal({ type: 'disable', employee: e, saving: false, error: '' })}
                    disabled={!e.is_active}
                    title={!e.is_active ? 'Déjà désactivé' : 'Désactiver'}
                  >
                    <span className="ButtonInline">
                      <IconTrash /> Désactiver
                    </span>
                  </button>
                </div>
              </div>
            ))}
            {sorted.length === 0 ? <div className="Muted">Aucun employé.</div> : null}
          </div>
        ) : null}
      </div>

      {modal?.type === 'edit' ? (
        <Modal
          title={modal.isNew ? 'Nouvel employé' : 'Modifier l’employé'}
          icon={<IconUsers />}
          onClose={() => setModal(null)}
          submitOnEnter
          onSubmit={async () => {
            if (modal.saving) return;
            if (modal.isNew && !String(modal.password || '').trim()) {
              setModal((m) => ({ ...m, error: 'Mot de passe requis.' }));
              return;
            }
            setModal((m) => ({ ...m, saving: true, error: '' }));
            const res = await saveEmployee(modal);
            if (!res.ok) {
              setModal((m) => ({ ...m, saving: false, error: res.error || 'Erreur' }));
              return;
            }
            setModal(null);
            refresh();
          }}
          footer={
            <>
              <button type="button" className="Button Button--ghost" onClick={() => setModal(null)} disabled={modal.saving}>
                Annuler
              </button>
              <button type="button" className="Button" disabled={modal.saving || !String(modal.email || '').trim()} onClick={async () => {
                if (modal.saving) return;
                if (modal.isNew && !String(modal.password || '').trim()) {
                  setModal((m) => ({ ...m, error: 'Mot de passe requis.' }));
                  return;
                }
                setModal((m) => ({ ...m, saving: true, error: '' }));
                const res = await saveEmployee(modal);
                if (!res.ok) {
                  setModal((m) => ({ ...m, saving: false, error: res.error || 'Erreur' }));
                  return;
                }
                setModal(null);
                refresh();
              }}>
                {modal.saving ? 'Enregistrement…' : 'Enregistrer'}
              </button>
            </>
          }
        >
          <div className="FormGrid">
            <div className="Field">
              <label className="Label">Nom</label>
              <input className="Input" value={modal.display_name} onChange={(e) => setModal((m) => ({ ...m, display_name: e.target.value }))} disabled={modal.saving} placeholder="ex: Martin" />
            </div>
            <div className="Field">
              <label className="Label">Email</label>
              <input className="Input" value={modal.email} onChange={(e) => setModal((m) => ({ ...m, email: e.target.value }))} disabled={modal.saving} placeholder="employe@omniscope.fr" />
            </div>
          </div>

          <div className="Field" style={{ marginTop: 10 }}>
            <label className="Label">Statut</label>
            <select
              className="Select"
              value={String(modal.staff_status || 'employee')}
              onChange={(e) => setModal((m) => ({ ...m, staff_status: e.target.value }))}
              disabled={modal.saving}
            >
              <option value="employee">Employé</option>
              <option value="freelance">Freelance</option>
              <option value="apprentice">Apprenti</option>
              <option value="intern">Stagiaire</option>
            </select>
          </div>

          <div className="Field" style={{ marginTop: 10 }}>
            <label className="Label">{modal.isNew ? 'Mot de passe' : 'Nouveau mot de passe (optionnel)'}</label>
            <input className="Input" value={modal.password} onChange={(e) => setModal((m) => ({ ...m, password: e.target.value }))} disabled={modal.saving} placeholder={modal.isNew ? 'Requis' : 'Laisser vide'} />
          </div>

          <div className="Field" style={{ marginTop: 10 }}>
            <label className="ButtonInline" style={{ gap: 10 }}>
              <input type="checkbox" checked={Boolean(modal.is_active)} onChange={(e) => setModal((m) => ({ ...m, is_active: Boolean(e.target.checked) }))} disabled={modal.saving} />
              <span>Compte actif</span>
            </label>
          </div>

          <div className="Field" style={{ marginTop: 10 }}>
            <label className="Label">Accès sites</label>
            <div className="InputRow" style={{ marginBottom: 10 }}>
              <input className="Input" value={modal.site_query} onChange={(e) => setModal((m) => ({ ...m, site_query: e.target.value }))} disabled={modal.saving} placeholder="Rechercher un site…" />
              <span className="Badge">{(modal.site_ids || []).length} sélectionné(s)</span>
            </div>
            <div className="Card" style={{ padding: 12, borderRadius: 12, border: '1px solid var(--border)', maxHeight: 220, overflow: 'auto' }}>
              {visibleSites.map((s) => {
                const checked = (modal.site_ids || []).some((x) => Number(x) === Number(s.id));
                return (
                  <label key={s.id} className="ButtonInline" style={{ gap: 10, display: 'flex', alignItems: 'center', padding: '6px 4px' }}>
                    <input type="checkbox" checked={checked} onChange={() => toggleSite(s.id)} disabled={modal.saving} />
                    <span className="OneLine">{s.name}</span>
                  </label>
                );
              })}
              {visibleSites.length === 0 ? <div className="Muted">Aucun site.</div> : null}
            </div>
          </div>

          {(modal.site_ids || []).length ? (
            <div className="Field" style={{ marginTop: 10 }}>
              <label className="Label">Permissions admin par module</label>
              <div className="Hint">Active “admin” pour autoriser les actions sensibles (création / suppression / envoi d’emails…).</div>
              <div style={{ display: 'grid', gap: 10, marginTop: 10 }}>
                {(modal.site_ids || [])
                  .map((sid) => Number(sid))
                  .filter((sid) => Number.isFinite(sid) && sid > 0)
                  .map((sid) => {
                    const siteName = allSites.find((s) => Number(s.id) === Number(sid))?.name || `Site #${sid}`;
                    const box = siteModulesById[String(sid)] || { status: 'idle', modules: [], error: '' };
                    const enabledModules = (box.modules || []).filter((m) => m && m.is_enabled).filter((m) => !['clients', 'sites', 'admin_settings'].includes(String(m.key)));
                    const selectedAdmin = modal.site_admin_modules?.[String(sid)] || [];
                    const set = new Set(selectedAdmin.map((x) => String(x)));
                    return (
                      <div key={sid} className="Card" style={{ padding: 12 }}>
                        <div className="Row" style={{ marginBottom: 8 }}>
                          <div className="TitleSm OneLine">{siteName}</div>
                          <span className="Badge">{set.size} admin</span>
                        </div>
                        {box.status === 'loading' ? <div className="Muted">Chargement des modules…</div> : null}
                        {box.status === 'error' ? <div className="Error">{box.error || 'Impossible de charger'}</div> : null}
                        {box.status === 'ready' ? (
                          enabledModules.length ? (
                            <div style={{ display: 'grid', gap: 8 }}>
                              {enabledModules.map((m) => (
                                <label key={String(m.key)} className="ButtonInline" style={{ gap: 10, justifyContent: 'space-between' }}>
                                  <span className="ButtonInline" style={{ gap: 10 }}>
                                    <span className="Dot" style={{ background: moduleColor(m.key, m.color), width: 10, height: 10, borderRadius: 999 }} />
                                    <span>{m.label}</span>
                                  </span>
                                  <input
                                    type="checkbox"
                                    checked={set.has(String(m.key))}
                                    onChange={() => toggleSiteModuleAdmin(sid, m.key)}
                                    disabled={modal.saving}
                                    title="Admin"
                                    aria-label={`Admin ${m.label}`}
                                  />
                                </label>
                              ))}
                            </div>
                          ) : (
                            <div className="Muted">Aucun module activé sur ce site.</div>
                          )
                        ) : null}
                      </div>
                    );
                  })}
              </div>
            </div>
          ) : null}

          {modal.error ? (
            <div className="Error" role="alert" style={{ marginTop: 10 }}>
              {modal.error}
            </div>
          ) : null}
        </Modal>
      ) : null}

      {modal?.type === 'disable' ? (
        <Modal
          title="Désactiver l’employé"
          icon={<IconAlertTriangle />}
          onClose={() => setModal(null)}
          footer={
            <>
              <button type="button" className="Button Button--ghost" onClick={() => setModal(null)} disabled={modal.saving}>
                Annuler
              </button>
              <button
                type="button"
                className="Button Button--danger"
                disabled={modal.saving}
                onClick={async () => {
                  const id = Number(modal.employee?.id);
                  if (!Number.isFinite(id) || id <= 0) return;
                  setModal((m) => ({ ...m, saving: true, error: '' }));
                  const res = await api.adminEmployeeDisable({ id });
                  if (!res.ok) {
                    setModal((m) => ({ ...m, saving: false, error: res.error || 'Erreur' }));
                    return;
                  }
                  setModal(null);
                  refresh();
                }}
              >
                {modal.saving ? 'Désactivation…' : 'Désactiver'}
              </button>
            </>
          }
        >
          <div className="Muted">
            Désactiver <strong>{employeeTitle(modal.employee)}</strong> ?
          </div>
          {modal.error ? (
            <div className="Error" role="alert" style={{ marginTop: 10 }}>
              {modal.error}
            </div>
          ) : null}
        </Modal>
      ) : null}
    </div>
  );
}
